import { Component, input, inject, computed, effect, Signal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BaseInfopageComponent, BaseInfopageUnitComponent } from "../base-infopage/base-infopage.component";
import { ShareAppartementInfoDto } from "src/models/estate/key-info.model";
import { InfoPageAccordionComponent } from "../components/info-page-accordion/info-page-accordion.component";
import { EvLogoLoaderComponent } from "src/app/shared-components/ev-logo-loader/ev-logo-loader.component";
import { BuildingsComponent } from "../components/buildings/buildings.component";
import { AccordionIdentity, AccordionStateClass } from "../models/accordion-state.class";
import { KeyInfoComponent } from "./key-info/key-info.component";
import { FunctionalUnitDataService } from "src/services/functional-unit/functionalunit-data.service";
import { InfoPageAddressHeaderComponent } from "../components/header/address-header.component";
import { ImagePackage } from "src/models/common/image.model";
import { KeyInfoDetailsComponent } from "./key-info-details/key-info-details.component";
import { SpecialConstraintsComponent } from "src/app/shared-components/specialconstraints/specialconstraints.component";
import { PhysicalExposuresComponent } from "../components/physical-exposures/physical-exposures.component";
import { UnitEnergyInfoComponent } from "../components/unit-energy-info/unit-energy-info.component";
import { BusinessManagerComponent } from "../components/business-manager/business-manager.component";
import { NearbySalesComponent } from "../components/nearby-sales/nearby-sales.component";
import { NearbySiteSalesComponent } from "../components/nearby-site-sales/nearby-site-sales.component";
import { PublicAddressInfoComponent } from "../components/public-address-info/public-address-info.component";
import { LinkHelperService, SolutionPage, UnitLinkType } from "src/services/shared/link-helper.service";
import { ValuationHistoryResidentialComponent } from "../components/valuation-history-residential/valuation-history-residential.component";
import { ValuationHistoryFilterComponent } from "../components/valuation-history-filter/valuation-history-filter.component";
import { ValueHistoryType } from "src/models/estate/valuation-history.model";
import { NavigationType } from "src/models/common/navigation-link.model";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";

@Component({
    selector: "ev-functional-unit",
    templateUrl: "./functional-unit.component.html",
    styleUrl: "./functional-unit.component.scss",
    imports: [
        BaseInfopageComponent,
        BuildingsComponent,
        KeyInfoComponent,
        InfoPageAccordionComponent,
        EvLogoLoaderComponent,
        InfoPageAddressHeaderComponent,
        KeyInfoDetailsComponent,
        SpecialConstraintsComponent,
        UnitEnergyInfoComponent,
        PhysicalExposuresComponent,
        BusinessManagerComponent,
        NearbySalesComponent,
        NearbySiteSalesComponent,
        PublicAddressInfoComponent,
        ValuationHistoryResidentialComponent,
        ValuationHistoryFilterComponent,
    ],
})
export class FunctionalUnitComponent extends BaseInfopageUnitComponent {
    private readonly linkHelperService = inject(LinkHelperService);
    private readonly functionalUnitDataService = inject(FunctionalUnitDataService);
    private readonly authService = inject(EvAuthorizationService);

    protected selectedFilters = signal<ValueHistoryType[]>([]);

    // UnitId hentes fra route parameter
    unitId = input.required<number | undefined>();

    loadErrorText = signal("");

    protected readonly valueHistoryAccordionStateClass;
    protected readonly energyAccordionStateClass;
    protected readonly keyInfoAccordionStateClass;
    protected readonly buildingAccordionStateClass;
    protected readonly specialConstraintsAccordionStateClass;
    protected readonly physicalRiskAccordionStateClass;
    protected readonly businessManagerAccordionStateClass;
    protected readonly nearbySalesAccordionStateClass;
    protected readonly nearbySiteSalesAccordionStateClass;
    protected readonly publicAddressInfoAccordionStateClass;
    private shareSalesAccordionOpened = signal(false);

    protected hasPhysicalExposuresRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_PhysicalExposure));

    NavigationType = NavigationType;

    protected menuItems = computed(() => {
        const keyInfo = this.keyInfo();

        if (!keyInfo) {
            return [];
        }

        const linkParams = {
            cadastre: keyInfo.parentCadastre,
            position: { lat: keyInfo.latitude, lng: keyInfo.longitude },
            organizationNumber: keyInfo.organizationNumber,
            unitId: keyInfo.unitId,
            estateType: keyInfo.estateType,
            estateId: keyInfo.parentEstateId,
            ownershipType: keyInfo.ownership,
            ownershipSubType: keyInfo.ownershipSubtype,
            address: keyInfo.unitAddress,
            unitLinkType: UnitLinkType.FunctionalUnit,
        };
        let x = this.linkHelperService.getLinks(SolutionPage.InfopageFunctionalUnit, linkParams);
        return x;
    });

    constructor() {
        super();
        effect((): void => {
            this.functionalUnitDataService.setUnitId(this.unitId());
        });
        this.accordionStateCollection = [
            (this.valueHistoryAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.valueHistoryResource,
                "Verdihistorikk",
                AccordionIdentity.ValueHistory,
                "Ingen verdihistorikk tilgjengelig for denne boenheten",
                "Klarte ikke å laste inn verdihistorikk for boenheten",
                null,
            )),
            (this.energyAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.energyUnitInfoResource,
                "Energibruk",
                AccordionIdentity.Energy,
                "Ingen energidata registrert for denne boenheten",
                "Klarte ikke å laste inn energidata for boenheten",
                (expanded: boolean) => this.onEnergyAccordionToggled(),
            )),
            (this.buildingAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.propertyInformationResource,
                "Bygninger",
                AccordionIdentity.Buildings,
                "Ingen bygninger registrert for denne boenheten",
                "Klarte ikke å laste inn bygninger for boenheten",
                (expanded: boolean) => this.onBuildingsAccordionToggled(),
                null,
                () => this.keyInfo()?.dataCounters?.numBuildings ?? null,
            )),
            (this.keyInfoAccordionStateClass = new AccordionStateClass(
                null,
                "Nøkkelinfo",
                AccordionIdentity.KeyInfo,
                "Ingen nøkkelinfo registrert for denne boenheten",
                "Klarte ikke å laste inn nøkkelinfo for boenheten",
                null,
            )),
            (this.physicalRiskAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.physicalRiskResource,
                "Fysisk risiko",
                AccordionIdentity.PhysicalRisk,
                "Ingen fysisk risiko registrert for denne boenheten",
                "Klarte ikke å laste inn fysisk risiko for boenheten",
                null,
                null,
                () => (this.hasPhysicalExposuresRole() ? this.keyInfo()?.dataCounters?.numPhysicalExposures ?? null : null),
            )),
            (this.specialConstraintsAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.specialConstraintsResource,
                "Spesielle forhold",
                AccordionIdentity.SpecialConstraints,
                "Ingen spesielle forhold registrert for denne boenheten",
                "Klarte ikke å laste inn spesielle forhold for boenheten",
                null,
                null,
                (input) => {
                    return input?.aggregatedSpecialConstraints?.length;
                },
            )),
            (this.businessManagerAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.businessManagerResource,
                "Forretningsfører",
                AccordionIdentity.BusinessManager,
                "Ingen forretningsfører registrert for denne boenheten",
                "Klarte ikke å laste inn forretningsfører for boenheten",
            )),
            (this.nearbySalesAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.nearbySalesResource,
                "Salg i nærområdet",
                AccordionIdentity.NearbySales,
                "Ingen salg i nærområdet registrert for denne boenheten",
                "Klarte ikke å laste inn salg i nærområdet for boenheten",
                (expanded: boolean) => this.onNearbySalesAccordionToggled(expanded),
            )),
            (this.nearbySiteSalesAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.nearbySiteSalesResource,
                "Tomtesalg",
                AccordionIdentity.NearbySiteSales,
                "Ingen tomtesalg registrert for denne boenheten",
                "Klarte ikke å laste inn tomtesalg for boenheten",
                (expanded: boolean) => this.onNearbySiteSalesAccordionToggled(expanded),
            )),
            (this.publicAddressInfoAccordionStateClass = new AccordionStateClass(
                this.functionalUnitDataService.publicAddressInfoResource,
                "Adresser",
                AccordionIdentity.PublicAddressInfo,
                "Ingen adresseinformasjon registrert for denne boenheten",
                "Klarte ikke å laste inn adresseinformasjon for boenheten",
                (expanded: boolean) => this.onPublicAddressInfoAccordionToggled(expanded),
                null,
                () => this.keyInfo()?.dataCounters?.numAddresses ?? null,
            )),
        ];
        this.valueHistoryAccordionStateClass.open();
    }

    keyInfoError = this.functionalUnitDataService.functionalUnitInfoErrorMessage;
    keyInfo: Signal<ShareAppartementInfoDto> = this.functionalUnitDataService.functionalUnitInfo;
    keyInfoIsLoading = this.functionalUnitDataService.functionalUnitInfoResource.isLoading;
    physicalExposures = this.functionalUnitDataService.physicalExposures;
    specialConstraints = this.functionalUnitDataService.aggregatedSpecialConstraints;
    reportUrls = this.functionalUnitDataService.reportUrls;
    imagePackages: Signal<ImagePackage[]> = this.functionalUnitDataService.imagePackages;
    foreclosureDiscount = this.functionalUnitDataService.foreclosureDiscount;
    neighbourhoodData = this.functionalUnitDataService.neighbourhoodData;

    staticMapInput = computed(() => {
        if (this.functionalUnitDataService.position()) {
            return { id: this.unitId(), iconEstateType: this.keyInfo().ownershipEstateSubTypeDisplayText, latlng: this.functionalUnitDataService.position() };
        }

        return null;
    });

    organization = computed(() => {
        if (this.keyInfo()?.organizationName && this.keyInfo()?.organizationNumber) {
            return this.keyInfo().organizationName + '/' + this.keyInfo().organizationNumber;
        }
        else if (this.keyInfo()?.organizationName) {
            return this.keyInfo()?.organizationName;
        }
        else if (this.keyInfo()?.organizationNumber) {
            return this.keyInfo()?.organizationNumber.toString();
        }

        return null;
    });

    onBuildingsAccordionToggled(): void {
        this.functionalUnitDataService.triggerLoadPropertyInformation();
    }
    onEnergyAccordionToggled(): void {
        this.functionalUnitDataService.triggerLoadEnergyUnitInfo();
    }
    onShareSalesAccordionToggled(expanded: boolean): void {
        this.shareSalesAccordionOpened.set(expanded);
    }
    onNearbySalesAccordionToggled(expanded: boolean): void {
        this.functionalUnitDataService.triggerLoadNearbySales();
    }
    onNearbySiteSalesAccordionToggled(expanded: boolean): void {
        this.functionalUnitDataService.triggerLoadNearbySiteSales();
    }
    onPublicAddressInfoAccordionToggled(expanded: boolean): void {
        this.functionalUnitDataService.triggerLoadPublicAddressInfo();
    }
}
